import {
  adaptChatMessagesToUIMessages,
  ChatContent,
  ChatFooter,
  ChatProvider,
  ChatScrollWrapper,
  useChildPlaybookMessages,
  useTrackActiveChatSession,
} from "@qw/chat";
import { useChatSessionQuery } from "@qw/graphql";
import { Empty, Spinner } from "@qw/ui";
import { useMemo } from "react";
import { useParams } from "react-router";

/**
 * Standalone chat session viewer that displays a chat session by ID.
 * This is useful for viewing chat sessions from external links (e.g., Grafana dashboards).
 */
export const ChatSessionView = () => {
  const { chatSessionId } = useParams<{ chatSessionId: string }>();

  const { data, loading, error } = useChatSessionQuery({
    variables: { id: chatSessionId! },
    skip: !chatSessionId,
  });

  // Merge messages from root session + child playbook sessions
  const { messages: mergedMessages, isLoading: isLoadingChildMessages } = useChildPlaybookMessages(
    data?.chatSession?.id,
    data?.chatSession?.messages || []
  );

  const initialMessages = useMemo(() => adaptChatMessagesToUIMessages(mergedMessages), [mergedMessages]);

  // Track this session as active and clear unread status when viewed
  useTrackActiveChatSession(data?.chatSession?.id);

  if (loading || isLoadingChildMessages) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <Empty
        icon="error"
        title="Error loading chat session"
        description={error.message}
        className="flex-1 justify-center"
      />
    );
  }

  if (!data?.chatSession) {
    return (
      <Empty
        icon="chat"
        title="Chat session not found"
        description="The chat session you're looking for doesn't exist or you don't have access to it."
        className="flex-1 justify-center"
      />
    );
  }

  return (
    <div className="flex h-full flex-col">
      <ChatProvider chatSession={data.chatSession} initialMessages={initialMessages}>
        <ChatScrollWrapper>
          <div className="border-b px-4 py-3">
            <h2 className="text-lg font-semibold">Chat Session</h2>
            {data.chatSession.title && <p className="text-sm text-muted-foreground">{data.chatSession.title}</p>}
          </div>
          <ChatContent />
          <ChatFooter />
        </ChatScrollWrapper>
      </ChatProvider>
    </div>
  );
};
